import React from 'react'
import styled from 'styled-components'
// @ts-ignore
import { QuestionMarkOutlined as QuestionMarkOutlinedIcon } from '@netbasequid/core'
import { ColDefs } from 'components/visualization/Table/types/groupTable'
import { GroupMetricStrings } from 'enums/GroupMetric'
import { MessageConst } from 'enums'
import i18n from 'i18n'
import TableSortOption from 'enums/TableSortOption'
import { ChannelInfos, MetricValues, TableContext } from './types'
import {
  metricTranslations,
  metricOrders,
  metricTooltipTranslations,
  multiLineHeaders
} from './constants'
import Profile from './Profile'
import FooterTitle from './FooterTitle'
import TooltipContent from './TooltipContent'
import HeaderText from './HeaderText'

const SingleLineCell = styled.div`
  white-space: nowrap;
`

const numberFormatter = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  // @ts-ignore
  // Don't know why Safari(15.6.1+) doesn't honor maximumFractionDigits.
  // Therefore, the experimental roundingPriority is added to force Safari to use the result calculated from maximumFractionDigits.
  roundingPriority: 'lessPrecision'
})

const formatValue = (value?: number) => {
  if (value === undefined) {
    return MessageConst.EM_DASH
  }
  if (value < 0) {
    return '*'
  }
  if (value > 0 && value < 0.01) {
    return '< 0.01'
  }
  return numberFormatter.format(value)
}

const formatPercentShare = (value?: number, total?: number) => {
  if (value === undefined || total === undefined || total === 0) {
    return MessageConst.EM_DASH
  }
  const percentShare = (value / total) * 100
  if (percentShare < 0) {
    return MessageConst.EM_DASH
  }
  if (percentShare > 0 && percentShare < 1) {
    return '<1%'
  }
  return `${percentShare.toFixed()}%`
}

const formatValueWithPercentShare = (
  value?: number,
  hasPercentShare?: boolean,
  grandTotals?: MetricValues,
  metric?: GroupMetricStrings
) => {
  const formattedValue = formatValue(value)
  if (!hasPercentShare || !grandTotals || !metric) {
    return formattedValue
  }
  const percentShare = formatPercentShare(value, grandTotals?.[metric])
  return percentShare ? `${formattedValue} (${percentShare})` : formattedValue
}

export const generateStaticColdefs = (
  isByNetworkType?: boolean
): ColDefs<ChannelInfos, TableContext> => [
  {
    field: 'channelName',
    customHeaderRender: () => <HeaderText title={i18n.t('channel')} />,
    cellProps: { textAlign: 'left', contentStyle: { minWidth: '170px' } },
    customCellRender: (_, { row }) => (
      <Profile channelInfos={row} showChannelTypeIcon={!isByNetworkType} />
    ),
    customGroupFooterRenders: [
      () => <FooterTitle title={i18n.t('total')} />,
      () => <FooterTitle title={i18n.t('average')} />
    ],
    customFooterRenders: [
      () => <FooterTitle title={i18n.t('grandTotal')} />,
      () => <FooterTitle title={i18n.t('grandAverage')} />
    ],
    sortable: true,
    defaultOrder: TableSortOption.ASCEND,
    comparator: (rowA, rowB) => {
      if (rowA.channelName > rowB.channelName) {
        return 1
      }
      if (rowA.channelName < rowB.channelName) {
        return -1
      }
      return 0
    }
  }
]

export const dynamicColdefs: ColDefs<
  MetricValues,
  TableContext,
  GroupMetricStrings
> = metricOrders.map((metric) => ({
  field: metric,
  customHeaderRender: () => <HeaderText title={i18n.t(metricTranslations[metric])} />,
  cellProps: { textAlign: 'right' },
  customCellRender: (cellValue, { hasPercentShare, grandTotals }) => (
    <SingleLineCell>
      {formatValueWithPercentShare(cellValue, hasPercentShare, grandTotals, metric)}
    </SingleLineCell>
  ),
  customGroupFooterRenders: [
    ({ hasPercentShare, metricTotals, grandTotals }) => (
      <SingleLineCell>
        {formatValueWithPercentShare(metricTotals[metric], hasPercentShare, grandTotals, metric)}
      </SingleLineCell>
    ),
    ({ metricAverages }) => <SingleLineCell>{formatValue(metricAverages[metric])}</SingleLineCell>
  ],
  customFooterRenders: [
    ({ hasPercentShare, metricTotals, grandTotals }) => (
      <SingleLineCell>
        {formatValueWithPercentShare(metricTotals[metric], hasPercentShare, grandTotals, metric)}
      </SingleLineCell>
    ),
    ({ metricAverages }) => <SingleLineCell>{formatValue(metricAverages[metric])}</SingleLineCell>
  ],
  sortable: true,
  defaultOrder: TableSortOption.DESCEND,
  comparator: (rowA, rowB) => {
    const valueA = rowA[metric] ?? -Infinity
    const valueB = rowB[metric] ?? -Infinity
    return valueA - valueB
  },
  headerProps: {
    icon: {
      render: <QuestionMarkOutlinedIcon fontSize={14} />,
      tooltipContent: <TooltipContent translationKey={metricTooltipTranslations[metric]} />
    },
    contentStyle: {
      justifyContent: 'flex-end'
    },
    textStyle: {
      textAlign: 'right',
      ...(multiLineHeaders.includes(metric) && { whiteSpace: 'normal', minWidth: '140px' })
    }
  },
  footerProps: {
    contentStyle: {
      justifyContent: 'flex-end'
    }
  }
}))
